import React, { useEffect, useState } from "react";
import LanguageSelection from "../../BatchNotification/LanguageSelection/LanguageSelection";
import { MessageLanguageRow } from "../MessageLanguageRow/MessageLanguageRow";
import { allLanguages } from "../../../languages";
import { useBatchMessageConfiguration } from "../../../hooks/useBatchMessageConfiguration";
import { Message } from "../../../common/batchNotificationInterface";
import BatchMessageLanguageDetailModel, {
	LanguageModel,
} from "../../../models/BatchMessage/BatchMessageLanguageDetailModel";
import {
	Card,
	CardContent,
	Grid,
	Tooltip,
	Icon,
	TableContainer,
	Table,
	Paper,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
} from "../../../common/MaterialComponent";
import { ButtonPrimary } from "../../../common/sharedStyle";
import "./MessageLanguageConfiguration.css";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { BatchMessageTypeEnum } from "../../../common/enum";
import { ConfirmationDialog } from "../../Confirmation/ConfirmationDialog";
import { ILanguage } from "../../../common/interfaces";
import BatchMessageDetailModel from "../../../models/BatchMessage/BatchMessageDetailModel";
import { ContentTemplate } from "../../../models/BatchMessage/ContentTemplate";
import { GetContentTemplates } from "../../../api/batchNotificationController";

export interface MessageLanguageRowProps {
	batchNotification: BatchMessageDetailModel;
	onBatchNotificationChange: React.Dispatch<
		React.SetStateAction<BatchMessageDetailModel>
	>;
	isReadOnly: boolean;
	setHasStep2ConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
	highlightRequiredDetail: boolean;
}

const MessageLanguageConfiguration: React.FC<MessageLanguageRowProps> = ({
	batchNotification,
	onBatchNotificationChange,
	isReadOnly,
	highlightRequiredDetail,
	setHasStep2ConfigChanged,
}) => {
	const strings = useGetComponentStringsContext().MessageLanguageConfiguration;
	// use reducer for all state logic
	const [state, dispatch] = useBatchMessageConfiguration();
	// state for confirmation logic
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(
		false
	);
	const [confirmationMessage, setConfirmationMessage] = useState<string>("");
	const [selectedRowIndex, setSelectedRowId] = useState<string>("");
	const [languageList, setLanguageList] = useState<ILanguage[]>([]);
	const [templates, setTemplates] = useState<ContentTemplate[]>([]);
	useEffect(() => {
		async function getTemplates() {
			const contentTemplates = await GetContentTemplates(
				batchNotification.country
			);
			if (contentTemplates.responseObj) {
				setTemplates(contentTemplates.responseObj.contents);
			}
		}
		if (batchNotification.batchMessageType === BatchMessageTypeEnum.WhatsApp) {
			getTemplates();
		}
	}, [batchNotification.country, batchNotification.batchMessageType]);

	useEffect(() => {
		const getMessageLanguageDetailToSave = () => {
			const messageWithLanguageDetail: BatchMessageLanguageDetailModel = new BatchMessageLanguageDetailModel();
			if (state.languages.length > 0) {
				const languageList: LanguageModel[] = [];
				state.items.forEach((messageDetail) => {
					messageDetail.messages.forEach((language) => {
						const model: LanguageModel = new LanguageModel();
						model.id = language.id ? language.id : "";
						model.code = language.languageCode;
						model.fileName = language.fileName;
						model.audioDuration = language.audioDuration;
						model.message = language.message ? language.message.trim() : "";
						model.messageTitle = messageDetail.title
							? messageDetail.title.trim()
							: "";
						languageList.push(model);
					});
				});
				messageWithLanguageDetail.languages = languageList;
			}
			return messageWithLanguageDetail;
		};
		onBatchNotificationChange((prevBatchMessageDetail) => {
			return {
				...prevBatchMessageDetail,
				batchMessageLanguageDetail: getMessageLanguageDetailToSave(),
			};
		});
	}, [onBatchNotificationChange, state.items, state.languages.length]);

	useEffect(() => {
		if (
			batchNotification.batchMessageLanguageDetail &&
			batchNotification.batchMessageLanguageDetail.languages &&
			batchNotification.batchMessageLanguageDetail.languages.length > 0
		) {
			let languageList: LanguageModel[] = JSON.parse(
				JSON.stringify(batchNotification.batchMessageLanguageDetail!.languages)
			);
			dispatch({
				type: "INITIALIZE_BATCH_MESSAGE",
				payload: {
					languageList: languageList,
				},
			});
		}
		// disabled eslint rule as useEffect needs to be called once on page load to initialize state
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onLanguageSelectionChange = (newLanguages: ILanguage[]) => {
		const languageModel = state.languages.find(
			(x: ILanguage) => !newLanguages.includes(x)
		);
		setLanguageList(newLanguages);

		let isMessageDetailExists: boolean = false;
		for (let index = 0; index < state.items.length; index++) {
			const messageDetail = state.items[index];
			let messageCount = messageDetail.messages.filter(
				(x) => x.languageCode === languageModel?.code && x.message
			).length;
			if (messageCount > 0) {
				isMessageDetailExists = true;
				break;
			}
		}
		// show confirmation dialog if message with language detail is added
		if (isMessageDetailExists) {
			openConfirmationPopup(strings.columnDeleteConfirmationMessage);
		} else {
			languageDetailChange(newLanguages);
		}
	};

	const onRemoveRowClicked = (rowId: string) => {
		const index = state.items.findIndex((i) => i.rowId === rowId);
		const isMessageDetailExists =
			index > -1
				? state.items[index].messages.filter((m) => m.message).length > 0
				: false;
		setSelectedRowId(rowId);
		if (isMessageDetailExists) {
			openConfirmationPopup(strings.rowDeleteConfirmationMessage);
		} else {
			deleteRowDetail(rowId);
		}
	};

	const onConfirmDeleteMessageDetail = (isDeleteRecord: boolean = false) => {
		if (isDeleteRecord) {
			if (selectedRowIndex !== "") {
				deleteRowDetail(selectedRowIndex);
			} else {
				languageDetailChange(languageList);
			}
		}
		setConfirmationMessage("");
		setSelectedRowId("");
		setLanguageList([]);
		setOpenConfirmationDialog(false);
	};

	function openConfirmationPopup(message: string) {
		setConfirmationMessage(message);
		setOpenConfirmationDialog(true);
	}

	function deleteRowDetail(rowId: string) {
		dispatch({
			type: "REMOVE_MESSAGE_ITEM",
			payload: {
				id: rowId,
			},
		});
		setHasStep2ConfigChanged(true);
	}

	function languageDetailChange(newLanguages: ILanguage[]) {
		dispatch({
			type: "SET_LANGUAGES",
			payload: {
				languages: newLanguages,
			},
		});
		setHasStep2ConfigChanged(true);
	}

	const onMessageTitleChanged = (rowId: string, title: string) => {
		dispatch({
			type: "SET_MESSAGE_ITEM_TITLE",
			payload: { id: rowId, title },
		});
		setHasStep2ConfigChanged(true);
	};

	const onMessageDetailChanged = (rowId: string, messageDetail: Message) => {
		dispatch({
			type: "SET_MESSAGE_ITEM_DETAIL",
			payload: {
				id: rowId,
				messageDetail: messageDetail,
			},
		});
		setHasStep2ConfigChanged(true);
	};

	const onTemplateSelected = (
		rowId: string,
		message: Message,
		languages: ILanguage[]
	) => {
		dispatch({
			type: "SET_MESSAGE_DETAIL_AND_LANGUAGE",
			payload: {
				id: rowId,
				message: message,
				languages: languages,
			},
		});
	};

	const addNewMessageItem = () => {
		dispatch({ type: "ADD_NEW_MESSAGE_ITEM" });
		setHasStep2ConfigChanged(true);
	};
	if (
		batchNotification.batchMessageType === BatchMessageTypeEnum.WhatsApp &&
		!state.items.length
	) {
		addNewMessageItem();
	}
	return (
		<div className="root" data-testid="languageConfiguration">
			<Card variant="outlined">
				<CardContent>
					<Grid container spacing={3}>
						<Grid item xs={11}>
							<Grid item xs={6}>
								{batchNotification.batchMessageType ===
									BatchMessageTypeEnum.SMS && (
									<LanguageSelection
										isReadOnly={isReadOnly}
										languageList={allLanguages}
										selectedLanguages={state.languages}
										onLanguageChange={(newLanguages) => {
											onLanguageSelectionChange(newLanguages);
										}}
									/>
								)}
							</Grid>
						</Grid>
						<Grid item xs={1}>
							{batchNotification.batchMessageType !==
								BatchMessageTypeEnum.WhatsApp && (
								<div className="addMessageBtnDiv">
									<Tooltip title={strings.newMessageToolTip}>
										<span>
											<ButtonPrimary
												variant="contained"
												color="primary"
												onClick={() => addNewMessageItem()}
												className="messageBtn"
												disabled={isReadOnly || state.languages.length === 0}
												data-testid="newMessageBtn"
											>
												<Icon>add</Icon>
											</ButtonPrimary>
										</span>
									</Tooltip>
								</div>
							)}
						</Grid>
					</Grid>
					<TableContainer component={Paper}>
						<Table
							className="table"
							aria-label="message language configuration"
						>
							{batchNotification.batchMessageType ===
								BatchMessageTypeEnum.SMS && (
								<TableHead>
									<TableRow className="tableRow">
										<TableCell>&nbsp;</TableCell>
										{state.languages.map((l) => (
											<TableCell align="center" key={l.code}>
												{l.name}
											</TableCell>
										))}
										<TableCell>&nbsp;</TableCell>
									</TableRow>
								</TableHead>
							)}
							<TableBody>
								{state.items.map((e) => (
									<MessageLanguageRow
										key={e.rowId}
										languages={state.languages}
										item={e}
										isReadOnly={isReadOnly}
										rowIndex={e.rowId}
										showRemoveRowButton={state.items.length > 1}
										highlightRequiredDetail={highlightRequiredDetail}
										isBatchMessageTypeSMS={
											batchNotification.batchMessageType! ===
											BatchMessageTypeEnum.SMS
										}
										isBatchMessageTypeWhatsApp={
											batchNotification.batchMessageType ===
											BatchMessageTypeEnum.WhatsApp
										}
										partitionKey={batchNotification.partitionKey}
										country={batchNotification.country}
										onTitleChanged={(title: string) =>
											onMessageTitleChanged(e.rowId, title)
										}
										onMessageChanged={(messageDetail: Message) =>
											onMessageDetailChanged(e.rowId, messageDetail)
										}
										onTemplateSelected={(message: Message) => {
											let messageLanguage;
											allLanguages.forEach((language) => {
												if (
													language.code === message.languageCode.split("_")[0]
												) {
													messageLanguage = language;
												}
											});
											if (!messageLanguage) {
												console.error("TEMPLATE MISSING LANGUAGE CODE");
												return;
											}
											onTemplateSelected(
												e.rowId,
												message,
												state.languages.includes(messageLanguage)
													? state.languages
													: [...state.languages, messageLanguage]
											);
										}}
										onRemoveClicked={() => onRemoveRowClicked(e.rowId)}
										templates={templates}
									/>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>
			{openConfirmationDialog && (
				<ConfirmationDialog
					open={openConfirmationDialog}
					message={confirmationMessage}
					closeDialog={(isDeleteRecord: boolean | undefined) =>
						onConfirmDeleteMessageDetail(isDeleteRecord)
					}
				></ConfirmationDialog>
			)}
		</div>
	);
};
export default MessageLanguageConfiguration;
